import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import DealCard from '../components/DealCard';
import { 
  ArrowLeft, Settings, Share2, Bookmark, ShoppingBag, 
  Grid, Trophy, Flame, Camera, Plus, ExternalLink
} from 'lucide-react';

const CATEGORIES = {
  beauty: { name: 'Beauty', icon: '💄', color: '#F9A8D4' },
  clothing: { name: 'Clothing', icon: '👕', color: '#7DD3FC' },
  shoes: { name: 'Shoes', icon: '👟', color: '#4ADE80' },
  home: { name: 'Home', icon: '🪑', color: '#FCD34D' },
  tech: { name: 'Tech', icon: '📱', color: '#A78BFA' },
  travel: { name: 'Travel', icon: '✈️', color: '#2DD4BF' },
  gifts: { name: 'Gifts', icon: '🎁', color: '#FB923C' },
  flash: { name: 'Flash Deals', icon: '⚡', color: '#F87171' },
};

// Grabbit Bunny Status Levels 🐰
const STATUS_LEVELS = {
  1: { name: "Base Bunny", icon: "🐰", color: "#9CA3AF" },
  2: { name: "Hop Watcher", icon: "👀", color: "#60A5FA" },
  3: { name: "Saver Bunny", icon: "💰", color: "#34D399" },
  4: { name: "Smart Hopper", icon: "🧠", color: "#FBBF24" },
  5: { name: "Deal Tracker", icon: "🎯", color: "#F472B6" },
  6: { name: "Trusted Buyer", icon: "⭐", color: "#A78BFA" },
  7: { name: "Community Helper", icon: "🤝", color: "#2DD4BF" },
  8: { name: "Deal Specialist", icon: "🔍", color: "#FB923C" },
  9: { name: "Expert Bunny", icon: "👑", color: "#EC4899" },
  10: { name: "Grabbit PRO", icon: "🏆", color: "#8B5CF6" },
};

const DealLocker = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [drops, setDrops] = useState([]);
  const [savedDeals, setSavedDeals] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [badges, setBadges] = useState({});
  const [userStatus, setUserStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('drops');
  const [selectedPurchaseCategory, setSelectedPurchaseCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  const isOwnLocker = currentUser?.user_id === userId;

  useEffect(() => {
    fetchLockerData();
  }, [userId]);

  const fetchLockerData = async () => {
    try {
      const [profileRes, dropsRes, purchasesRes, badgesRes] = await Promise.all([
        api.get(`/users/${userId}`),
        api.get(`/users/${userId}/drops`),
        api.get(`/users/${userId}/verified-purchases`).catch(() => ({ data: [] })),
        api.get(`/users/${userId}/badges`).catch(() => ({ data: {} })),
      ]);
      
      setProfile(profileRes.data);
      setDrops(dropsRes.data);
      setPurchases(purchasesRes.data);
      setBadges(badgesRes.data);

      // Get saved deals if own locker
      if (isOwnLocker) {
        const savedRes = await api.get('/deals/saved');
        setSavedDeals(savedRes.data);
        
        // Get status
        const statusRes = await api.get('/my-status');
        setUserStatus(statusRes.data);
      }
    } catch (error) {
      toast.error('Failed to load locker');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDeal = async (dealId, isSaved) => {
    try {
      if (isSaved) {
        await api.delete(`/deals/drops/${dealId}/save`);
        setSavedDeals(savedDeals.filter(d => d.drop_id !== dealId));
      } else {
        await api.post(`/deals/drops/${dealId}/save`);
      }
      setDrops(drops.map(d => 
        d.drop_id === dealId 
          ? { ...d, is_saved: !isSaved, saves_count: d.saves_count + (isSaved ? -1 : 1) }
          : d
      ));
      toast.success(isSaved ? 'Removed from saved' : 'Deal saved!');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  // Calculate stats
  const totalSavings = purchases.reduce((acc, p) => acc + (p.savings || 0), 0);
  const categoryCounts = drops.reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, {});
  const topCategories = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // Get purchase categories
  const purchaseCategories = [...new Set(purchases.map(p => p.category))];

  // Filter purchases by category
  const filteredPurchases = selectedPurchaseCategory 
    ? purchases.filter(p => p.category === selectedPurchaseCategory)
    : purchases;

  const statusInfo = userStatus || { rank: 1, name: 'Base', icon: '⚪', color: '#9CA3AF' };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-heading font-semibold">Deal Locker</span>
            {isOwnLocker ? (
              <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
                <Settings className="w-5 h-5" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon">
                <Share2 className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Profile Header */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-start gap-6">
          {/* Avatar with status ring */}
          <div 
            className="w-20 h-20 rounded-2xl p-0.5"
            style={{ background: `linear-gradient(135deg, ${statusInfo.color}, #8B5CF6)` }}
          >
            <div className="w-full h-full rounded-2xl bg-background overflow-hidden flex items-center justify-center">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold">{profile.name?.charAt(0)}</span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold flex items-center gap-2">
              {profile.name}
              {profile.streak_days >= 7 && <Flame className="w-5 h-5 text-orange-500" />}
            </h1>
            
            {/* Status Badge */}
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-1"
              style={{ backgroundColor: `${statusInfo.color}20`, color: statusInfo.color }}
            >
              {statusInfo.icon} {statusInfo.name}
              {statusInfo.rank >= 9 && <span className="ml-1">PRO</span>}
            </span>

            {profile.bio && (
              <p className="text-sm text-muted-foreground mt-2">{profile.bio}</p>
            )}
            
            {/* Stats */}
            <div className="flex items-center gap-4 mt-3">
              <div className="text-center">
                <span className="font-bold">{drops.length}</span>
                <span className="text-xs text-muted-foreground ml-1">Drops</span>
              </div>
              <div className="text-center">
                <span className="font-bold">{purchases.length}</span>
                <span className="text-xs text-muted-foreground ml-1">Catches</span>
              </div>
              {totalSavings > 0 && (
                <div className="text-center">
                  <span className="font-bold text-green-500">${totalSavings.toFixed(0)}</span>
                  <span className="text-xs text-muted-foreground ml-1">Saved</span>
                </div>
              )}
              {profile.streak_days > 0 && (
                <div className="text-center">
                  <span className="font-bold text-orange-500">{profile.streak_days}</span>
                  <span className="text-xs text-muted-foreground ml-1">🔥 Streak</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Badges */}
        {Object.keys(badges).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {Object.entries(badges).map(([id, badge]) => (
              <span 
                key={id}
                className="px-3 py-1.5 rounded-full bg-muted text-sm"
                title={badge.desc}
              >
                {badge.icon} {badge.name}
              </span>
            ))}
          </div>
        )}

        {/* Top Categories */}
        {topCategories.length > 0 && (
          <div className="flex gap-2 mt-4">
            {topCategories.map(([catId, count]) => {
              const cat = CATEGORIES[catId];
              return (
                <span 
                  key={catId}
                  className="px-3 py-1.5 rounded-full text-sm text-black"
                  style={{ backgroundColor: cat?.color }}
                >
                  {cat?.icon} {count} drops
                </span>
              );
            })}
          </div>
        )}

        {/* Status Progress (own locker only) */}
        {isOwnLocker && userStatus && userStatus.next_level_points && (
          <div className="mt-4 glass rounded-xl p-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Progress to next level</span>
              <span style={{ color: statusInfo.color }}>
                {userStatus.points} / {userStatus.next_level_points} pts
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <motion.div 
                className="h-full rounded-full"
                style={{ backgroundColor: statusInfo.color }}
                initial={{ width: 0 }}
                animate={{ width: `${userStatus.progress_to_next}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Next: {STATUS_LEVELS[statusInfo.rank + 1]?.name || 'Max level reached!'}
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 border-b border-border/50">
        <div className="flex">
          <button
            onClick={() => setActiveTab('drops')}
            className={`flex-1 py-3 text-center transition-colors relative ${
              activeTab === 'drops' ? 'text-primary' : 'text-muted-foreground'
            }`}
            data-testid="drops-tab"
          >
            <Grid className="w-5 h-5 mx-auto" />
            <span className="text-xs mt-1 block">Drops</span>
            {activeTab === 'drops' && (
              <motion.div layoutId="locker-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          {isOwnLocker && (
            <button
              onClick={() => setActiveTab('saved')}
              className={`flex-1 py-3 text-center transition-colors relative ${
                activeTab === 'saved' ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid="saved-tab"
            >
              <Bookmark className="w-5 h-5 mx-auto" />
              <span className="text-xs mt-1 block">Saved</span>
              {activeTab === 'saved' && (
                <motion.div layoutId="locker-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          )}
          <button
            onClick={() => setActiveTab('purchases')}
            className={`flex-1 py-3 text-center transition-colors relative ${
              activeTab === 'purchases' ? 'text-primary' : 'text-muted-foreground'
            }`}
            data-testid="purchases-tab"
          >
            <Camera className="w-5 h-5 mx-auto" />
            <span className="text-xs mt-1 block">Purchases</span>
            {activeTab === 'purchases' && (
              <motion.div layoutId="locker-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Drops Tab */}
          {activeTab === 'drops' && (
            <motion.div
              key="drops"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {drops.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Grid className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No deals dropped yet</p>
                  {isOwnLocker && (
                    <Button 
                      onClick={() => navigate('/drop-deal')}
                      className="mt-4 rounded-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Drop Your First Deal
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {drops.map((deal) => (
                    <DealCard 
                      key={deal.drop_id}
                      deal={deal}
                      category={CATEGORIES[deal.category]}
                      onSave={() => handleSaveDeal(deal.drop_id, deal.is_saved)}
                      onWatch={() => {}}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Saved Tab */}
          {activeTab === 'saved' && isOwnLocker && (
            <motion.div
              key="saved"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {savedDeals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Bookmark className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No saved deals</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {savedDeals.map((deal) => (
                    <DealCard 
                      key={deal.drop_id}
                      deal={deal}
                      category={CATEGORIES[deal.category]}
                      onSave={() => handleSaveDeal(deal.drop_id, true)}
                      onWatch={() => {}}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Purchases Tab - Gallery by Category */}
          {activeTab === 'purchases' && (
            <motion.div
              key="purchases"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {purchases.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Camera className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-2">No verified purchases yet</p>
                  <p className="text-sm">Upload purchase photos to build trust and earn points!</p>
                  {isOwnLocker && (
                    <Button 
                      onClick={() => navigate('/upload-purchase')}
                      className="mt-4 rounded-full"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Upload Purchase
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Category Filter */}
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    <button
                      onClick={() => setSelectedPurchaseCategory(null)}
                      className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                        !selectedPurchaseCategory 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      All ({purchases.length})
                    </button>
                    {purchaseCategories.map(catId => {
                      const cat = CATEGORIES[catId];
                      const count = purchases.filter(p => p.category === catId).length;
                      return (
                        <button
                          key={catId}
                          onClick={() => setSelectedPurchaseCategory(catId)}
                          className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                            selectedPurchaseCategory === catId 
                              ? 'text-black' 
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                          style={selectedPurchaseCategory === catId ? { backgroundColor: cat?.color } : {}}
                        >
                          {cat?.icon} {cat?.name} ({count})
                        </button>
                      );
                    })}
                  </div>

                  {/* Purchase Gallery Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {filteredPurchases.map((purchase, index) => {
                      const cat = CATEGORIES[purchase.category];
                      return (
                        <motion.div
                          key={purchase.purchase_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="relative aspect-square rounded-2xl overflow-hidden group cursor-pointer bg-muted"
                        >
                          <img 
                            src={purchase.image_url} 
                            alt={purchase.product_name}
                            className="w-full h-full object-cover"
                          />
                          
                          {/* Overlay on hover */}
                          <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                            <p className="text-white font-medium text-sm truncate">{purchase.product_name}</p>
                            <p className="text-white/70 text-xs">{purchase.brand}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-white font-bold">${purchase.price_paid}</span>
                              {purchase.savings > 0 && (
                                <span className="text-green-400 text-xs">Saved ${purchase.savings.toFixed(0)}</span>
                              )}
                            </div>
                            {purchase.store_url && (
                              <a 
                                href={purchase.store_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 text-xs text-white/70 mt-1 hover:text-white"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {purchase.store_name}
                              </a>
                            )}
                            {purchase.expert_name && (
                              <p className="text-xs text-violet-400 mt-1">
                                via {purchase.expert_name}
                              </p>
                            )}
                          </div>

                          {/* Category Badge */}
                          <div 
                            className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs text-black"
                            style={{ backgroundColor: cat?.color }}
                          >
                            {cat?.icon}
                          </div>

                          {/* Verified Badge */}
                          {purchase.is_verified && (
                            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                              <span className="text-xs">✓</span>
                            </div>
                          )}

                          {/* Discount Badge */}
                          {purchase.discount_percent > 0 && (
                            <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-green-500 text-white text-xs font-bold">
                              -{purchase.discount_percent}%
                            </div>
                          )}
                        </motion.div>
                      );
                    })}

                    {/* Add Purchase Card (own locker only) */}
                    {isOwnLocker && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={() => navigate('/upload-purchase')}
                        className="aspect-square rounded-2xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-all"
                      >
                        <Camera className="w-8 h-8 text-muted-foreground mb-2" />
                        <span className="text-xs text-muted-foreground">Add Purchase</span>
                      </motion.div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DealLocker;
